// App.js
import React, { useEffect, Suspense, lazy } from "react";
import { Routes, Route, Outlet } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { initGA, trackPage } from "./utils/analytics";

import MainLayout from "./layouts/MainLayout";
import TeacherLayout from "./layouts/TeacherLayout";
import ParentLayout from "./layouts/ParentLayout";
import StudentLayout from "./layouts/StudentLayout";
import AccountLayout from "./layouts/AccountLayout";

import HomePage from "./pages/home/HomePage";
import ProgramsPage from "./pages/programs/ProgramsPage";
import ProgramDetailPage from "./pages/programs/ProgramDetailPage";
import ContactUsPage from "./pages/contact/ContactUsPage";

import BlogsPage from "./pages/blogs/BlogsPage";
import BlogDetailPage from "./pages/blogs/BlogDetailPage";

import routePaths from "./routes/routePaths";
import RichPage from "./pages/common/RichPage";

import RequireAdmin from "./components/auth/RequireAdmin";
import RequireAuth from "./components/auth/RequireAuth";
import RequireRole from "./components/auth/RequireRole";

import NotFoundPage from "./pages/common/NotFoundPage";
import TeacherClassesList from "./pages/teachers/TeacherClassesList";
import TeacherClassDetail from "./pages/teachers/TeacherClassDetail";

import ScrollToTop from "./components/ui/ScrollToTop";
import DrawLineDemo from "./pages/common/demo";
import DebugAppCheckToken from "./pages/common/DebugAppCheckToken";

import { useDynamicI18n } from "./hooks/useDynamicI18n";
import { useRealtimeI18n } from "./hooks/useRealtimeI18n";
import { useTranslation } from "react-i18next";
import { useI18nReady } from "./hooks/useI18nReady";
import LoginPage from "./pages/users/LoginPage";
import UserRegister from "./pages/users/UserRegisterPage";
import StudentAssignments from "./pages/students/StudentAssignments";
import ParentChildrenPage from "./pages/parents/ParentChildrenPage";

// =============== CHUNK LOADERS ===============
function ChunkLoader({ label }) {
  return <div className="p-6 text-center text-menu">Loading {label}…</div>;
}

// =============== ADMIN: LAZY (chunk "admin") ===============
const AdminLayout = lazy(() => import(/* webpackChunkName: "admin" */ "./layouts/AdminLayout"));
const AdminPages = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminPages"));
const AdminDashboard = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminDashboard"));
const AdminBlogsList = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminBlogsList"));
const EditBlogContent = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/EditBlogContent"));
const AdminHighlights = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminHighlights"));
const AdminTestimonials = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminTestimonials"));
const AdminPrograms = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminPrograms"));
const EditProgram = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/EditProgram"));
const AdminTests = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminTests"));
const AdminTestEditor = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminTestEditor"));
const EditPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/EditPage"));
const AdminSuccessHighlights = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminSuccessHighlights"));
const AdminUsers = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminUsers"));
const AdminSectionEditor = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminSectionEditor"));
const AdminSections = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminSections"));
const AdminSiteInfo = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminSiteInfo"));
const AdminI18nEditor = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/components/AdminI18nEditor"));
const AdminSeoMeta = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminSeoMeta"));
const AdminLogs = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminLogs"));
const AdminClassesPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminClassesPage"));

// =============== TEST: LAZY (chunk "test") ===============
const TestsPage = lazy(() => import(/* webpackChunkName: "test" */ "./pages/tests/TestsPage"));
const TestPage = lazy(() => import(/* webpackChunkName: "test" */ "./pages/tests/TestPage"));
const TestIntro = lazy(() => import(/* webpackChunkName: "test" */ "./pages/tests/TestIntroPage"));
const ResultPage = lazy(() => import(/* webpackChunkName: "test" */ "./pages/tests/TestResultPage"));

// =============== USER: LAZY (chunk "user") ===============
// const UserRegister = lazy(() => import(/* webpackChunkName: "user" */ "./pages/users/UserRegisterPage"));
// const LoginPage = lazy(() => import(/* webpackChunkName: "user" */ "./pages/users/LoginPage"));
const ProfilePage = lazy(() => import(/* webpackChunkName: "user" */ "./pages/users/ProfilePage"));
const AccountSettingsPage = lazy(() => import(/* webpackChunkName: "user" */ "./pages/users/AccountSettingsPage"));
const MyResultsPage = lazy(() => import(/* webpackChunkName: "user" */ "./pages/users/MyResultsPage"));
const ForgotPasswordPage = lazy(() => import(/* webpackChunkName: "user" */ "./pages/users/ForgotPasswordPage"));

function App() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const i18nReady = useI18nReady();

  useDynamicI18n();
  useRealtimeI18n(i18n.language);

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackPage(location.pathname + location.search);
  }, [location]);

  if (!i18nReady)
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <img src="/logo.png" alt="Logo" className="h-12 w-auto mb-6 animate-pulse" />
        <div className="flex space-x-2 mb-4">
          <div className="w-3 h-3 bg-primary rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-primary/70 rounded-full animate-bounce delay-150"></div>
          <div className="w-3 h-3 bg-primary/40 rounded-full animate-bounce delay-300"></div>
        </div>
        <div className="text-lg text-primary font-medium tracking-wide">Loading...</div>
      </div>
    );

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<MainLayout />}>
          <Route path="/demo" element={<DrawLineDemo />} />
          <Route path="/app-check" element={<DebugAppCheckToken />} />

          {/* PUBLIC (không lazy) */}
          <Route path={routePaths.home} element={<HomePage />} />
          <Route path={routePaths.programs} element={<ProgramsPage />} />
          <Route path={routePaths.programDetail} element={<ProgramDetailPage />} />
          <Route path={routePaths.contact} element={<ContactUsPage />} />
          <Route path={routePaths.blog} element={<BlogsPage />} />
          <Route path={routePaths.blogDetail} element={<BlogDetailPage />} />

          {/* Rich pages */}
          <Route path={routePaths.teachers} element={<RichPage slug="teachers" />} />
          <Route path={routePaths.results} element={<RichPage slug="results" />} />
          <Route path={routePaths.about} element={<RichPage slug="about" />} />
          <Route path={routePaths.schedule} element={<RichPage slug="schedule" />} />
          <Route path="/:slug" element={<RichPage />} />

          {/* TEST (lazy chunk "test") */}
          <Route element={<Suspense fallback={<ChunkLoader label="test" />}><Outlet /></Suspense>}>
            <Route path={routePaths.testList} element={<TestsPage />} />
            <Route path={routePaths.testPage} element={<TestPage />} />
            <Route path={routePaths.testIntro} element={<TestIntro />} />
            <Route path={routePaths.testPageResult} element={<ResultPage />} />
          </Route>
          <Route path={routePaths.userRegister} element={<UserRegister />} />
          <Route path={routePaths.login} element={<LoginPage />} />

          {/* USER (lazy) */}
          <Route element={<Suspense fallback={<ChunkLoader label="user" />}><Outlet /></Suspense>}>

            <Route
              path={routePaths.user}
              element={
                <RequireAuth>
                  <AccountLayout />
                </RequireAuth>
              }
            >
              <Route path={routePaths.myResults} element={<MyResultsPage />} />
              <Route path={routePaths.profile} element={<ProfilePage />} />
              <Route path={routePaths.accountSettings} element={<AccountSettingsPage />} />
            </Route>
            <Route path={routePaths.forgotPassword} element={<ForgotPasswordPage />} />
          </Route>

          {/* TEACHER */}
          <Route
            path="/teacher"
            element={
              <RequireAuth>
                <RequireRole roles={["teacher", "admin"]}>
                  <TeacherLayout />
                </RequireRole>
              </RequireAuth>
            }
          >
            <Route path="classes" element={<TeacherClassesList />} />
            <Route path="classes/:id" element={<TeacherClassDetail />} />
          </Route>
          <Route
            path="/students"
            element={
              <RequireAuth>
                <RequireRole roles={["teacher", "admin", "student"]}>
                  <AccountLayout />
                </RequireRole>
              </RequireAuth>
            }
          >
            <Route path={routePaths.STUDENT_ASSIGNMENTS} element={<StudentAssignments />} />

          </Route>

          {/* PARENT */}
          <Route
            path="/parent"
            element={
              <RequireAuth>
                <RequireRole roles={["parent", "admin"]}>
                  <ParentLayout />
                </RequireRole>
              </RequireAuth>
            }
          >
            <Route index element={<ParentChildrenPage />} />
            <Route path="children" element={<ParentChildrenPage />} />
          </Route>

          {/* STUDENT */}
          <Route
            path="/student"
            element={
              <RequireAuth>
                <RequireRole roles={["student", "admin"]}>
                  <StudentLayout />
                </RequireRole>
              </RequireAuth>
            }
          >
            {/* Add student routes here */}
          </Route>
        </Route>

        {/* ADMIN (lazy chunk "admin") */}
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <Suspense fallback={<ChunkLoader label="admin" />}>
                <AdminLayout />
              </Suspense>
            </RequireAdmin>
          }
        >
          <Route index element={<AdminUsers />} />
          <Route path="pages" element={<AdminPages />} />
          <Route path="pages/:id" element={<EditPage />} />
          <Route path="blog" element={<AdminBlogsList />} />
          <Route path="blog/:id" element={<EditBlogContent />} />
          <Route path="highlights" element={<AdminHighlights />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="sections/:id" element={<AdminSectionEditor />} />
          <Route path="programs" element={<AdminPrograms />} />
          <Route path="programs/:id" element={<EditProgram />} />
          <Route path="tests" element={<AdminTests />} />
          <Route path="tests/:id" element={<AdminTestEditor />} />
          <Route path="sections" element={<AdminSections />} />
          <Route path="success-highlights" element={<AdminSuccessHighlights />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="analytics" element={<AdminDashboard />} />
          <Route path="site-info" element={<AdminSiteInfo />} />
          <Route path="translate" element={<AdminI18nEditor />} />
          <Route path="seo-meta" element={<AdminSeoMeta />} />
          <Route path="logs" element={<AdminLogs />} />
          <Route path="classes" element={<AdminClassesPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

export default App;


// const prefetchAdmin = () => import(/* webpackChunkName: "admin", webpackPrefetch: true */ "./layouts/AdminLayout");
// const prefetchTest = () => import(/* webpackChunkName: "test", webpackPrefetch: true */ "./pages/tests/TestsPage");
// const prefetchUser = () => import(/* webpackChunkName: "user", webpackPrefetch: true */ "./pages/users/LoginPage");
// // <Link to="/admin" onMouseEnter={prefetchAdmin}>Admin</Link>
